import { DatePipe } from "@angular/common";
import { MatDateFormats } from "@angular/material/core";
import moment from 'moment';

/**
 * Converts 24-hour time format to 12-hour format.
 * The function takes a string in 'HH:MM' format and converts it into
 * a 12-hour format string with AM or PM suffix.
 * 
 * @param time24 Time string in 24-hour format ('HH:MM').
 * @returns Time string in 12-hour format ('HH:MM AM/PM').
 */
export function formatTime24To12(time24: string): string {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
}


/**
 * Converts a date string from 'YYYY-MM-DD' format to 'MM-DD-YYYY' format.
 * This function assumes the input string is a valid date in 'YYYY-MM-DD' format.
 * 
 * @param dateString The date string in 'YYYY-MM-DD' format.
 * @returns The date string in 'MM-DD-YYYY' format.
 */
export function convertYYYYMMDDToMMDDYYYY(dateString: string): string {
  const [year, month, day] = dateString.split("-");
  return `${month}-${day}-${year}`;
}



/**
 * Converts a Date object to a string in 'YYYY-MM-DD' format.
 * This function assumes the date in the Date object is in the local timezone
 * and formats it accordingly, ignoring any conversion to UTC.
 * 
 * @param date The Date object to format.
 * @returns A string representing the date in 'YYYY-MM-DD' format.
 */
export function convertDateToISODateString(date): string {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    const formattedMonth = month < 10 ? `0${month}` : `${month}`;
    const formattedDay = day < 10 ? `0${day}` : `${day}`;
  
    return `${year}-${formattedMonth}-${formattedDay}`;
  }
  



/**
 * Converts a date string in 'YYYY-MM-DD' format to a Date object using Moment.js.
 * This function assumes the input string is a valid date in 'YYYY-MM-DD' format.
 * 
 * @param dateString The date string in 'YYYY-MM-DD' format.
 * @returns The corresponding Date object or null if the input string is invalid.
 */
export function convertISODateStringToDate(dateString: string): Date | null {
  if (!dateString) {
    return null;
  }

  const date = moment(dateString, 'YYYY-MM-DD', true);

  // Validate that the date is valid using Moment.js
  if (!date.isValid()) {
    return null;
  }

  return date.toDate();
}

  

/**
 * Converts a date string from an Angular Material Datepicker to 'YYYY-MM-DD' format.
 * This function assumes the input is a valid date string from the Datepicker.
 * 
 * @param datePickerDate The date string from the Angular Material Datepicker (e.g., 'Mon Jan 01 2024 00:00:00 GMT-0600 (Central Standard Time)').
 * @returns A string representing the date in 'YYYY-MM-DD' format.
 */
export function convertDatePickerDateToISODateString(datePickerDate: any): string {
  if (datePickerDate === null || datePickerDate === undefined) {
    return null;
  }

  const date = new Date(datePickerDate);
  if (isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}


/**
 * Converts a Date object to a string in the format 'MMM DD, YYYY'.
 * This function returns null if the input is not a valid Date object.
 * 
 * @param date The Date object to format.
 * @returns A string representing the date in 'MMM DD, YYYY' format, or null if the input is invalid.
 */
export function formatDateToMMMDDYYYY(date: Date): string | null {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }

  return moment(date).format('MMM DD, YYYY');
}
